import cx from 'classnames';
import { useCallback, type VoidFunctionComponent } from 'react';
import type { Column, ComponentPropsGetterR, SortingRule } from 'react-table';

import Locator from '../../../../../shared/Locator';
import { useQuery } from '../../../../contexts/RouterContext';
import { IconAndNode, icons } from '../../../generic/Icon';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import { useSnippetsWithQueryParams } from '../hooks';
import type { Snippet, SnippetsQueryParams } from '../../../../../shared/snippets';

import ConfidenceBadge from './ConfidenceBadge';
import LicenseIssueCounts from './LicenseIssueCounts';

import styles from './SnippetsTable.module.less';

const defaultSortMode: SortingRule[] = [{
  id: 'package',
  desc: false,
}];

const snippetKindIcons = {
  snippet: icons.receipt,
  file: icons.fileOutline,
  dependency: icons.box,
};

const columns: Array<Column<Snippet>> = [
  {
    id: 'package',
    Header: <TableHeader title="Package" />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      // Snippets don't contain the raw package name so we need to parse it out of the Locator.
      const packageName = Locator.parse(snippet.id)?.package ?? 'Unknown';
      const icon = snippetKindIcons[snippet.kind];
      return (
        <div>
          <IconAndNode icon={icon} marginAmount="0.5em">
            <span>{packageName}</span>
          </IconAndNode>
        </div>
      );
    },
    resizable: false,
    sortable: false,
    // Ensure the text doesn't overflow and instead truncates with an ellipsis.
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'version',
    Header: <TableHeader title="Version" />,
    //  Snippets don't contain the raw package version, we need to parse them out of a Locator
    accessor: (row: Snippet) => {
      return Locator.parse(row.id)?.revision ?? '?.?.?';
    },
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'issues',
    Header: <TableHeader title="License Issues" />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      return <LicenseIssueCounts issueCounts={snippet.issueCounts.licensing} />;
    },
    resizable: false,
    sortable: false,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'confidence',
    Header: <TableHeader title="Conf." />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      return <ConfidenceBadge matchPercentage={snippet.highestMatchPercentage} />;
    },
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'matches',
    Header: <TableHeader title="Matches" />,
    accessor: (row: Snippet) => row.matches.length,
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
];

type Props = {
  userRevisionLocator: string;
};

/**
 * Component which renders the table view of the snippets.
 */
const SnippetsTable: VoidFunctionComponent<Props> = ({ userRevisionLocator }) => {
  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  //  Fetch the snippets. This hook already manages the query params itself, so we don't need to pass them
  const { data: snippets, isLoading: isLoadingSnippets } = useSnippetsWithQueryParams(userRevisionLocator);

  //  This is how we apply a click event + styling to the row itself.
  const getTableRowProps = useCallback((_rowState, rowInfo: { original: Snippet }) => ({
    className: cx(styles.tableRow, {
      [styles.tableRowActive]: rowInfo && queryParams?.selectedSnippetId === rowInfo?.original?.id,
    }),
    onClick: () => setQuery({ ...queryParams, selectedSnippetId: rowInfo?.original?.id }),
  }), [queryParams, setQuery]) as ComponentPropsGetterR;

  return (
    <div className={styles.table}>
      <SimpleReactTable
        data={snippets?.results ?? []}
        columns={columns}
        isLoading={isLoadingSnippets}
        defaultSortMode={defaultSortMode}
        minRows={10}
        //  TODO: this will likely change in the future.
        isSortable={false}
        getTrProps={getTableRowProps}
      />
    </div>
  );
};

export default SnippetsTable;
